import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import {
    BankValidationResponse,
    DirectDebitDetails,
    UpdateDirectDebitRequest
} from '../../common/CommonTypes';
import { PaymentApi } from 'api/PaymentApi';
import { TFunction } from 'i18next';

import { setBankDetails as setBankDetailsAction } from 'state/slices/customerSlice';
import { useAppDispatch, useAppSelector } from 'state/hooks';
import { AccountType } from 'Constants/Common';
import { useBankDetails } from './useBankDetails';
import { BankDetailsErrors } from './BankDetailsForm';

export function useBankDetailsForm({
    onSuccess,
    t,
    initialAgreements = {},
    requireAgreements = false,
    onBack,
    showPaymentDay = true,
    showBankInputSection,
    accountType,
    referenceNumber,
    businessPartnerId,
    includeConfirmationValidation = false
}: {
    onSuccess: (details: DirectDebitDetails) => void;
    t: TFunction<'translation', undefined>;
    initialAgreements?: {
        agreedDirectDebit?: boolean;
        agreedDirectDebitDueDate?: boolean;
    };
    requireAgreements?: boolean;
    onBack?: () => void;
    showPaymentDay?: boolean;
    showBankInputSection?: boolean;
    accountType: string;
    referenceNumber?: string;
    businessPartnerId: number;
    includeConfirmationValidation?: boolean;
}) {
    const dispatch = useAppDispatch();
    const customerEmail = useAppSelector((state) => state.customer.email) ?? '';
    const vehicles = useAppSelector((s) => s.vehicles.vehicleList);
    const scrollDivRef = useRef<null | HTMLDivElement>(null);
    const navigate = useNavigate();
    const { getAccessTokenSilently } = useAuth0();
    const [addDisbursement, setAddDisbursement] = useState(false);
    const {
        bankDetails,
        onChange,
        onPaymentDayChange,
        onSortCodeChange,
        daysOptions,
        validateBankDetails
    } = useBankDetails();
    const [bankDetailErrors, setBankDetailErrors] = useState<BankDetailsErrors>(
        {}
    );
    const [validateBankAccount, setValidateBankAccount] = useState(false);
    const [updatedDirectDebitDetails, setUpdatedDirectDebitDetails] =
        useState<DirectDebitDetails>();
    const [addDisbursementDetails, setAddDisbursementDetails] =
        useState<DirectDebitDetails>();
    const [agreedDirectDebit, setAgreedDirectDebit] = useState(
        initialAgreements.agreedDirectDebit || false
    );
    const [agreedDirectDebitDueDate, setAgreedDirectDebitDueDate] = useState(
        initialAgreements.agreedDirectDebitDueDate || false
    );
    const updateDirectDebit = {
        businessPartnerId: businessPartnerId,
        bankName: null
    };
    const [isConfirmed, setIsConfirmed] = useState(false);
    const [confirmationError, setConfirmationError] = useState('');

    useEffect(() => {
        scrollDivRef.current?.scrollIntoView();
    }, []);
    const updateDirectDebitSuccess = (data: BankValidationResponse) => {
        if (data.isSuccess && updatedDirectDebitDetails) {
            dispatch(
                setBankDetailsAction({
                    accountHolderName:
                        updatedDirectDebitDetails.accountHolderName,
                    accountNumber: updatedDirectDebitDetails.accountNumber,
                    sortCode: `${updatedDirectDebitDetails.sortCode1}${updatedDirectDebitDetails.sortCode2}${updatedDirectDebitDetails.sortCode3}`,
                    businessPartnerId:
                        updatedDirectDebitDetails.businessPartnerId,
                    bankName: updatedDirectDebitDetails.bankName,
                    accountType: accountType,
                    referenceNumber: referenceNumber
                })
            );
            onSuccess?.(updatedDirectDebitDetails);
        } else {
            updateDirectDebitFailure();
        }
    };
    const updateDirectDebitFailure = () => {
        const newError = {
            ...bankDetailErrors,
            bankValidation: t('direct_debit_guarantee.update-bank-failed')
        };
        setBankDetailErrors(newError);
    };
    const updateDirectDebitBankDetails = async () => {
        if (!updatedDirectDebitDetails) {
            return;
        }

        const token = await getAccessTokenSilently();
        const controller = new AbortController();
        new PaymentApi().UpdateDirectDebit(
            createUpdateDirectDebitRequest(updatedDirectDebitDetails),
            token,
            updateDirectDebitSuccess,
            updateDirectDebitFailure,
            controller.signal
        );
    };
    //update disbursement with collection bank details
    const updateDisbursementSuccess = () => {
        dispatch(
            setBankDetailsAction({
                accountHolderName: addDisbursementDetails.accountHolderName,
                accountNumber: addDisbursementDetails.accountNumber,
                sortCode: `${addDisbursementDetails.sortCode1}${addDisbursementDetails.sortCode2}${addDisbursementDetails.sortCode3}`,
                businessPartnerId: addDisbursementDetails.businessPartnerId,
                bankName: '',
                accountType: accountType,
                referenceNumber: referenceNumber
            })
        );
        onSuccess?.(addDisbursementDetails);
    };
    const updateDisbursementFailure = (error: string) => {
        const newError = {
            ...bankDetailErrors,
            bankValidation:
                t('direct_debit_guarantee.update-bank-failed') + ' ' + error
        };
        setBankDetailErrors(newError);
    };
    const updateDisbursementBankDetails = async () => {
        if (!addDisbursementDetails) {
            return;
        }

        const token = await getAccessTokenSilently();
        const controller = new AbortController();

        new PaymentApi().UpdateDirectDebit(
            createUpdateDirectDebitRequest(addDisbursementDetails),
            token,
            updateDisbursementSuccess,
            updateDisbursementFailure,
            controller.signal
        );
    };

    const createUpdateDirectDebitRequest = (
        directDebitDetails: DirectDebitDetails
    ): UpdateDirectDebitRequest => ({
        ...directDebitDetails,
        ...{
            policies: vehicles.map((vehicle) => ({
                productRefNumbers: vehicle.policies.map(
                    (policy) => policy.reference
                ),
                vehicleRegNumber: vehicle.vrn,
                customerEmail
            }))
        }
    });
    //end update disbursement with collection bank details
    const updateBankDetiails = async (bankName: string) => {
        if (bankName && updatedDirectDebitDetails) {
            setUpdatedDirectDebitDetails({
                ...updatedDirectDebitDetails,
                businessPartnerId: updateDirectDebit.businessPartnerId,
                bankName: bankName,
                accountType: accountType,
                referenceNumber: referenceNumber
            });
            await updateDirectDebitBankDetails();
        }
    };
    const successCallback = async (data: BankValidationResponse) => {
        if (data.isSuccess && validateBankAccount) {
            await updateBankDetiails(data.text || '');
            setValidateBankAccount(false);
        } else {
            setValidateBankAccount(false);
            const newError = {
                ...bankDetailErrors,
                bankValidation: t('validation-failed') + ' ' + data.text
            };
            setBankDetailErrors(newError);
        }
    };
    const failureCallback = (error: string) => {
        setValidateBankAccount(false);
        const newError = {
            ...bankDetailErrors,
            bankValidation: t('validation-failed') + ' ' + error
        };
        setBankDetailErrors(newError);
    };
    const validateDirectDebitBankDetails = async () => {
        const token = await getAccessTokenSilently();
        const controller = new AbortController();

        if (validateBankAccount === true) {
            new PaymentApi().validateDirectDebit(
                updatedDirectDebitDetails,
                token,
                successCallback,
                failureCallback,
                controller.signal
            );
        }
        return () => {
            controller.abort();
        };
    };

    useEffect(() => {
        if (!addDisbursement) {
            validateDirectDebitBankDetails();
        }
    }, [validateBankAccount, addDisbursement]);

    useEffect(() => {
        if (addDisbursement) {
            updateDisbursementBankDetails();
        }
    }, [addDisbursement === true]);

    const getErrors = (): string[] => {
        const errors = Object.values(bankDetailErrors).filter(
            (v): v is string => Boolean(v)
        );

        // Only include confirmation error if there are no bank detail errors
        if (errors.length === 0 && confirmationError) {
            errors.push(confirmationError);
        }

        return errors;
    };

    const validateBankDetailsForm = () => {
        const validationErrors: BankDetailsErrors = {};
        let isValid = true;

        if (
            requireAgreements &&
            (!agreedDirectDebit || !agreedDirectDebitDueDate)
        ) {
            validationErrors.directDebitPolicy = t(
                'direct_debit_guarantee.policy_acceptance_required'
            );
        }

        const {
            isValid: isBankDetailsValid,
            validationErrors: bankValidationErrors
        } = validateBankDetails();

        if (!isBankDetailsValid) {
            Object.assign(validationErrors, bankValidationErrors);
            isValid = false;
        }

        setBankDetailErrors(validationErrors);
        return isValid;
    };

    const onSubmit = () => {
        setUpdatedDirectDebitDetails({
            accountHolderName: bankDetails.accountHolderName,
            sortCode1: bankDetails.sortCode[0].value,
            sortCode2: bankDetails.sortCode[1].value,
            sortCode3: bankDetails.sortCode[2].value,
            accountNumber: bankDetails.accountNumber,
            selectedPaymentDay: bankDetails.paymentDay,
            accountType: accountType,
            businessPartnerId: updateDirectDebit.businessPartnerId,
            referenceNumber: referenceNumber
        });

        setConfirmationError('');

        const isValid = validateBankDetailsForm();
        if (isValid) {
            // Only check confirmation if bank details are valid
            if (includeConfirmationValidation && !isConfirmed) {
                setConfirmationError(
                    t('repayment_setup.toggle_confirmation_error')
                );
                return;
            }
            setValidateBankAccount(true);
        }
    };

    const validateConfirmation = () => {
        if (includeConfirmationValidation && !isConfirmed) {
            setConfirmationError(
                t('repayment_setup.toggle_confirmation_error')
            );
            return false;
        }
        setConfirmationError('');
        return true;
    };

    const onYesButtonClick = () => {
        setAddDisbursement(true);
        setAddDisbursementDetails({
            ...addDisbursementDetails,
            businessPartnerId: businessPartnerId,
            bankName: '',
            accountHolderName:
                updatedDirectDebitDetails?.accountHolderName || '',
            sortCode1: updatedDirectDebitDetails?.sortCode1 || '',
            sortCode2: updatedDirectDebitDetails?.sortCode2 || '',
            sortCode3: updatedDirectDebitDetails?.sortCode3 || '',
            accountNumber: updatedDirectDebitDetails?.accountNumber || '',
            selectedPaymentDay:
                updatedDirectDebitDetails?.selectedPaymentDay || '31',
            accountType: AccountType.Disbursement
        } as DirectDebitDetails);
    };

    return {
        scrollDivRef,
        bankDetails,
        bankDetailErrors,
        setBankDetailErrors,
        validateBankAccount,
        setValidateBankAccount,
        updatedDirectDebitDetails,
        setUpdatedDirectDebitDetails,
        agreedDirectDebit,
        setAgreedDirectDebit,
        agreedDirectDebitDueDate,
        setAgreedDirectDebitDueDate,
        isConfirmed,
        setIsConfirmed,
        confirmationError,
        setConfirmationError,
        onChange,
        onPaymentDayChange,
        onSortCodeChange,
        getErrors,
        daysOptions,
        onSubmit,
        onYesButtonClick,
        validateBankDetails: validateBankDetailsForm,
        validateConfirmation,
        onBack: onBack || (() => navigate(-1)),
        showPaymentDay,
        showBankInputSection
    };
}
